import { useCallback } from 'react';
import useNotification from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { getErrorMessage } from '@/utils/errorUtils';
import { getScopeErrorMessage } from '../utils/getScopeErrorMessage';
import routingService from '@/pages/Business/pages/Shippings/services/routing.service';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models/routing.types';
import { shippingNumberSearchSchema } from '../schemas/shippingNumberSearch.schema';

const SHIPPING_NOT_FOUND = 'Pedido no encontrado';

export interface UseSearchShippingParams {
  logisticOperatorId: string;
}

export interface UseSearchShippingResult {
  search: (number: number) => Promise<ShippingForRouting | null>;
  shipping: ShippingForRouting | undefined;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

export function useSearchShipping({
  logisticOperatorId,
}: UseSearchShippingParams): UseSearchShippingResult {
  const { showNotification } = useNotification();

  const [fetchByNumber, { data: shipping, isFetching: isLoading, reset }] =
    routingService.useLazyGetShippingByNumberForRoutingQuery();

  const search = useCallback(
    async (number: number): Promise<ShippingForRouting | null> => {
      const parsed = shippingNumberSearchSchema.safeParse({ number });
      if (!parsed.success) {
        const msg = parsed.error.errors[0]?.message ?? 'Número de pedido inválido';
        showNotification(msg, NotificationSeverity.warning);
        return null;
      }

      const num = parsed.data.number;
      try {
        const result = await fetchByNumber({
          number: num,
          logisticOperatorId: logisticOperatorId.trim() || '',
        }).unwrap();

        if (!result) {
          showNotification(SHIPPING_NOT_FOUND, NotificationSeverity.warning);
          return null;
        }
        return result;
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        const message =
          status === 404
            ? SHIPPING_NOT_FOUND
            : getScopeErrorMessage(err) ??
              getErrorMessage(err as Error, 'Error al buscar el pedido');
        showNotification(message, NotificationSeverity.error);
        return null;
      }
    },
    [logisticOperatorId, fetchByNumber, showNotification]
  );

  return { search, shipping, isLoading, error: null, reset };
}
